import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'
import { AlertTriangle, Check, X, Trash2, Loader2, Video, Pencil } from 'lucide-react'
import type { Approval } from '../../../shared/types/chat'
import { getRiskColor, getRiskGlow } from '../utils'
import { DataRow, AnimatedChevron } from '@tendbot/ui'
import AccountPill from './AccountPill'
import AccountSheet from './AccountSheet'
import { expandCollapse } from '@tendbot/ui'

/** Check if the expandable section would render any content. */
function hasExpandableContent(approval: Approval): boolean {
  const info = approval.extendedInfo
  if (!info) return false
  if (approval.type === 'booking' && info.restaurant) return true
  // Calendar details are now shown inline — only expand if there's a delete warning
  if (approval.type === 'calendar-delete' && info.event) return true
  if (info.email && (info.email.cc?.length || info.email.bcc?.length || info.email.body)) return true
  if (info.params && !info.email && !info.event) return true
  return false
}

interface ApprovalCardProps {
  approval: Approval
  messageId: string
  onApprove: (messageId: string, connectionId?: string, editedBody?: string) => void
  onDeny: (messageId: string) => void
}

export default function ApprovalCard({ approval, messageId, onApprove, onDeny }: ApprovalCardProps) {
  const { t } = useTranslation('chat')
  const [expanded, setExpanded] = useState(false)
  const [processing, setProcessing] = useState<'approving' | 'denying' | null>(null)
  const [selectedConnectionId, setSelectedConnectionId] = useState(approval.extendedInfo?.selectedConnectionId)
  const [accountSheetOpen, setAccountSheetOpen] = useState(false)
  const originalBody = approval.extendedInfo?.email?.body
  const [editingBody, setEditingBody] = useState(false)
  const [editedBody, setEditedBody] = useState(originalBody ?? '')
  const isEdited = originalBody !== undefined && editedBody !== originalBody

  const accounts = approval.extendedInfo?.availableAccounts

  const handleApproveClick = () => {
    setProcessing('approving')
    onApprove(messageId, selectedConnectionId, isEdited ? editedBody : undefined)
  }

  const handleDenyClick = () => {
    setProcessing('denying')
    onDeny(messageId)
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`glass-card rounded-xl overflow-hidden border-l-4 ${getRiskColor(approval.risk)} ${
        approval.status === 'pending' ? getRiskGlow(approval.risk) : ''
      } ${approval.status === 'approved' ? 'animate-gold-pulse' : ''} ${
        approval.status === 'denied' ? 'animate-deny-flash' : ''
      }`}
    >
      <div className="p-4">
        {/* Risk Badge */}
        <div className="flex items-center justify-between mb-3">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${getRiskColor(approval.risk)}`}>
            {approval.risk === 'high' && <AlertTriangle size={10} className="inline mr-1" />}
            {t('approval.risk', { level: approval.risk })}
          </span>
          {approval.status !== 'pending' && (
            <motion.span
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              className={`text-xs ${approval.status === 'approved' ? 'text-emerald' : 'text-rose'}`}
            >
              {approval.status === 'approved' ? `\u2713 ${t('approval.approved')}` : `\u2715 ${t('approval.denied')}`}
              {approval.status === 'approved' && (isEdited || approval.extendedInfo?.email?.editedBody) && (
                <span className="text-gold/70 ml-1">({t('approval.edited')})</span>
              )}
            </motion.span>
          )}
        </div>

        <h4 className="text-ivory font-medium text-sm mb-1">{approval.title}</h4>
        <p className="text-pearl/60 text-xs whitespace-pre-line">{approval.details}</p>

        {/* Account selector or service badge */}
        {accounts && accounts.length > 0 ? (
          <div className="mt-1.5">
            <AccountPill
              accounts={accounts}
              selectedId={selectedConnectionId}
              onTap={() => setAccountSheetOpen(true)}
              disabled={approval.status !== 'pending'}
            />
            <AccountSheet
              open={accountSheetOpen}
              title={approval.type === 'email' || approval.extendedInfo?.email
                ? t('approval.sendFrom')
                : t('approval.useCalendarFrom')}
              accounts={accounts}
              selectedId={selectedConnectionId}
              onSelect={setSelectedConnectionId}
              onClose={() => setAccountSheetOpen(false)}
            />
          </div>
        ) : approval.extendedInfo?.service ? (
          <span className="inline-block mt-1 text-[10px] text-pearl/60 tracking-wide">
            via {approval.extendedInfo.service}
          </span>
        ) : null}

        {/* Inline email summary (To + Subject visible without expanding) */}
        {approval.extendedInfo?.email && (
          <div className="mt-2 space-y-1">
            {approval.extendedInfo.email.replyingTo && (
              <DataRow
                label={t('approval.detail.replyingTo')}
                value={`"${approval.extendedInfo.email.replyingTo.subject}" — ${approval.extendedInfo.email.replyingTo.from}`}
              />
            )}
            {approval.extendedInfo.email.to && (
              <DataRow label={t('approval.detail.to')} value={approval.extendedInfo.email.to} />
            )}
            {approval.extendedInfo.email.subject && (
              <DataRow label={t('approval.detail.subject')} value={approval.extendedInfo.email.subject} />
            )}
          </div>
        )}

        {/* Inline event summary (title, time changes, attendees, meet link) */}
        {!approval.extendedInfo?.email && approval.extendedInfo?.event && (
          <div className="mt-2 space-y-1">
            {approval.extendedInfo.event.title && (
              <DataRow label={t('approval.detail.event')} value={approval.extendedInfo.event.title} />
            )}
            {approval.type === 'calendar-update' && approval.extendedInfo.changes?.oldTime && (
              <DataRow label={t('approval.detail.currentTime')} value={approval.extendedInfo.changes.oldTime} valueClassName="text-pearl/60 line-through" />
            )}
            {approval.type === 'calendar-update' && approval.extendedInfo.changes?.newTime && (
              <DataRow label={t('approval.detail.newTime')} value={approval.extendedInfo.changes.newTime} valueClassName="text-gold" />
            )}
            {approval.type !== 'calendar-update' && approval.extendedInfo.event.time && (
              <DataRow label={t('approval.detail.time')} value={approval.extendedInfo.event.time} />
            )}
            {approval.extendedInfo.event.attendees && approval.extendedInfo.event.attendees.length > 0 && (
              <DataRow label={t('approval.detail.attendees')} value={approval.extendedInfo.event.attendees.join(', ')} />
            )}
            {approval.extendedInfo.event.addMeetLink && (
              <div className="flex items-center gap-1.5 text-xs text-emerald">
                <Video size={11} />
                <span>{t('approval.detail.meetLink')}</span>
              </div>
            )}
          </div>
        )}

        {/* Extended info - expandable (only if there's content to show) */}
        {approval.extendedInfo && hasExpandableContent(approval) && (
          <div className="mt-2">
            <button
              onClick={() => setExpanded(prev => !prev)}
              className="flex items-center gap-1 text-xs text-gold/70 hover:text-gold transition-colors"
            >
              <AnimatedChevron isOpen={expanded} size={12} className="text-gold/70" />
              {expanded ? t('approval.hideDetails') : t('approval.showDetails')}
            </button>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  {...expandCollapse}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                    {approval.type === 'booking' && approval.extendedInfo.restaurant && (
                      <>
                        <DataRow label={t('approval.detail.restaurant')} value={approval.extendedInfo.restaurant.name} />
                        <DataRow label={t('approval.detail.rating')} value={approval.extendedInfo.restaurant.rating} valueClassName="text-gold" />
                        <DataRow label={t('approval.detail.estCost')} value={approval.extendedInfo.pricing?.estimatedCost} />
                      </>
                    )}
                    {approval.type === 'calendar-delete' && approval.extendedInfo.event && (
                      <div className="flex items-center gap-1.5 text-xs text-rose/80 mt-1">
                        <Trash2 size={11} />
                        <span>{t('approval.undoWarning')}</span>
                      </div>
                    )}
                    {approval.extendedInfo.email && (
                      <>
                        {approval.extendedInfo.email.cc && approval.extendedInfo.email.cc.length > 0 && (
                          <DataRow label={t('approval.detail.cc')} value={approval.extendedInfo.email.cc.join(', ')} />
                        )}
                        {approval.extendedInfo.email.bcc && approval.extendedInfo.email.bcc.length > 0 && (
                          <DataRow label={t('approval.detail.bcc')} value={approval.extendedInfo.email.bcc.join(', ')} />
                        )}
                        {originalBody && (
                          <div className="mt-2">
                            <div className="flex items-center gap-2">
                              <span className="text-pearl/60 text-xs">{t('approval.detail.body')}</span>
                              {isEdited && (
                                <span className="text-[10px] text-gold/70 font-medium">({t('approval.edited')})</span>
                              )}
                              {approval.status === 'pending' && !editingBody && (
                                <button
                                  onClick={() => setEditingBody(true)}
                                  className="text-pearl/60 hover:text-gold transition-colors ml-auto"
                                  aria-label={t('approval.editBody')}
                                >
                                  <Pencil size={11} />
                                </button>
                              )}
                            </div>
                            {editingBody && approval.status === 'pending' ? (
                              <textarea
                                value={editedBody}
                                onChange={(e) => setEditedBody(e.target.value)}
                                className="w-full mt-1 p-2 rounded-lg bg-midnight/60 border border-white/10 text-pearl/80 text-xs whitespace-pre-line resize-y min-h-[80px] focus:border-gold/40 focus:outline-none transition-colors"
                                rows={Math.min(12, Math.max(3, editedBody.split('\n').length + 1))}
                                autoFocus
                              />
                            ) : (
                              <p
                                className={`text-pearl/60 text-xs whitespace-pre-line mt-0.5 ${
                                  approval.status === 'pending' ? 'cursor-pointer hover:text-pearl/80 transition-colors' : ''
                                }`}
                                onClick={() => approval.status === 'pending' && setEditingBody(true)}
                              >
                                {(isEdited ? editedBody : originalBody).length > 500
                                  ? (isEdited ? editedBody : originalBody).slice(0, 500) + '...'
                                  : (isEdited ? editedBody : originalBody)}
                              </p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {approval.extendedInfo.params && !approval.extendedInfo.email && (
                      <>
                        {Object.entries(approval.extendedInfo.params).map(([key, value]) => (
                          <DataRow key={key} label={key} value={String(value)} />
                        ))}
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Action Buttons */}
        {approval.status === 'pending' && (
          <AnimatePresence mode="wait">
            {processing ? (
              <motion.div
                key="processing"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`mt-4 py-2.5 px-4 rounded-lg flex items-center justify-center gap-2.5 ${
                  processing === 'approving'
                    ? 'bg-gold/15 border border-gold/30'
                    : 'bg-rose/15 border border-rose/30'
                }`}
              >
                <motion.span
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                >
                  <Loader2
                    size={14}
                    className={processing === 'approving' ? 'text-gold' : 'text-rose'}
                  />
                </motion.span>
                <span className={`text-sm font-medium ${
                  processing === 'approving' ? 'text-gold' : 'text-rose'
                }`}>
                  {processing === 'approving'
                    ? approval.type === 'email' ? t('approval.processing.email')
                    : approval.type === 'booking' ? t('approval.processing.booking')
                    : approval.type === 'calendar-update' ? t('approval.processing.calendarUpdate')
                    : approval.type === 'calendar-delete' ? t('approval.processing.calendarDelete')
                    : t('approval.processing.generic')
                  : t('approval.processing.declining')}
                </span>
              </motion.div>
            ) : (
              <motion.div key="action-buttons" className="flex gap-2 mt-4">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleDenyClick}
                  className="py-2 px-3 rounded-full bg-slate-mid/50 text-pearl/70 text-sm font-medium hover:bg-rose/20 hover:text-rose transition-colors flex items-center justify-center gap-1.5"
                >
                  <X size={14} />
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleApproveClick}
                  className="flex-1 py-2 px-4 rounded-full bg-gold text-midnight text-sm font-semibold hover:bg-gold-light transition-colors flex items-center justify-center gap-2"
                >
                  <Check size={14} /> {t('approval.approve')}
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        )}

      </div>
    </motion.div>
  )
}
